import { Text } from '@chakra-ui/react'
import CheckBox from 'components/svg/CheckBox'
import { Body1 } from 'components/typography'
import Link from 'next/link'
import { ReactNode } from 'react'

import { OMENS_RECLAME_AQUI_URL } from '../../../omensSeoTechnical/head'
import { OMENS_PAGES } from '../../../omensSeoTechnical/pages'
import { GeoFaqItem, GeoLink } from '../types'

/**
 * The home, PRD section 7 (Morgan, 24/10/2026). The summary is the PRD text. The FAQ keeps the 5 current
 * questions (their current markup; the first one without "prescrição", PRD section 4) and adds the 5 of the PRD.
 * `answer` is the plain text of what is shown: the JSON-LD FAQPage uses it.
 */

export interface HomeFaqItem extends GeoFaqItem {
  /** The current answer's markup, kept as it is; without it, `answer` is rendered. */
  body?: ReactNode
}

export interface HomeGeo {
  meta: { title: string; description: string }
  summary: string
  conditionLinks: { title: string; href: string }[]
  statsSource: string
  datafolha: {
    title: string
    stats: { title: string; description: string }[]
    source: string
    link: { label: string; href: string }
  }
  faq: HomeFaqItem[]
}

export const HOME_SUMMARY =
  'A Omens é uma plataforma brasileira de saúde masculina online, criada em 2020 em São Paulo. Reúne urologistas, dermatologistas e psicólogos que atendem por vídeo, telefone ou mensagem, a partir de R$ 79. Trata ejaculação precoce, disfunção erétil, perda de libido, próstata aumentada, queda de cabelo, acne e emagrecimento, com tratamentos personalizados entregues em casa em embalagem discreta. Mais de 35 mil brasileiros já se consultaram.'

/** The summary in at most 155 characters, for the meta description (Morgan, 24/10/2026). */
export const HOME_META_DESCRIPTION =
  'A Omens é uma plataforma brasileira de saúde masculina online: urologistas, dermatologistas e psicólogos por vídeo, telefone ou mensagem, desde R$ 79.'

/** "Condições que tratamos" (PRD 7.2): the 4 hero shortcuts, to the pillar pages, plus 5 more. */
export const HOME_CONDITION_LINKS: { title: string; href: string }[] = [
  { title: 'Ejaculação precoce', href: '/ejaculacao-precoce' },
  { title: 'Cuidados com a pele e cabelo', href: '/dermatologia' },
  { title: 'Problemas de ereção', href: '/disfuncao-eretil' },
  { title: 'Libido em baixa', href: '/perda-de-libido' },
  { title: 'Próstata aumentada', href: '/prostata-aumentada' },
  { title: 'PrEP: prevenção do HIV', href: '/prep' },
  { title: 'Queda de cabelo', href: '/calvicie' },
  { title: 'Acne', href: '/acne' },
  { title: 'Emagrecimento', href: '/emagrecimento' }
]

/** Under the numbers (PRD section 6: confirmed by Morgan). */
export const HOME_STATS_SOURCE = 'Dados internos Omens, desde 2021'

/** "Pesquisas Omens + Datafolha" (PRD 7.3), numbers of the 2020 survey (1.813 men). */
export const HOME_DATAFOLHA = {
  title: 'Pesquisas Omens + Datafolha',
  stats: [
    { title: '48%', description: 'dos homens brasileiros já tiveram dificuldade para controlar a ejaculação' },
    {
      title: '38%',
      description: 'dos brasileiros que tiveram relações nos últimos 2 anos enfrentaram problemas de ereção'
    },
    { title: '64%', description: 'dos homens com problemas de ereção nunca procuraram um especialista' }
  ],
  source: 'Datafolha/Omens, 2020: pesquisa online com 1.813 homens de todo o Brasil',
  link: { label: 'Conheça as pesquisas Omens + Datafolha', href: '/problemas-de-erecao-o-grande-tabu' } as GeoLink
}

export const HOME_FAQ: HomeFaqItem[] = [
  {
    question: 'Omens, o que é isto?',
    answer:
      'A Omens é uma plataforma digital que ajuda homens adultos a tomarem o controle de seus problemas sexuais. Somos uma ferramenta para consultas médicas: colocamos o paciente em contato com urologistas especializados em sexologia e depois possibilitamos a entrega de medicamentos a domicílio (o médico indica o tratamento na consulta) com a ajuda de uma rede de farmácias parceiras.'
  },
  {
    question: 'Como consultar online um(a) médico(a) na Omens?',
    answer:
      'Para consultar um(a) médico(a) na Omens você precisa: preencher o formulário online disponível aqui; agendar sua consulta, escolhendo o meio que quiser: por chat, telefone ou videochamada; confirmar sua consulta disponibilizando suas informações pessoais e configurando o seu espaço na plataforma.',
    body: (
      <>
        <Body1>Para consultar um(a) médico(a) na Omens você precisa:</Body1>

        <Body1>
          <CheckBox w="12px" mr="6px" mb="3px" fill="phthalo" isChecked />
          Preencher o formulário online disponível{' '}
          <Text as="u" display="inline">
            <Link prefetch={false} href="/consultar?origin=sexual-health">
              aqui
            </Link>
          </Text>
        </Body1>

        <Body1>
          <CheckBox w="12px" mr="6px" mb="3px" fill="phthalo" isChecked />
          Agendar sua consulta, escolhendo o meio que quiser: por chat, telefone ou videochamada.
        </Body1>
        <Body1>
          <CheckBox w="12px" mr="6px" mb="3px" fill="phthalo" isChecked />
          Confirmar sua consulta disponibilizando suas informações pessoais e configurando o seu espaço na plataforma.
        </Body1>
      </>
    )
  },
  {
    question: 'Como remarcar ou cancelar uma consulta na Omens?',
    answer:
      'As consultas por vídeo ou telefone podem ser reagendadas ou canceladas entrando em contato com o nosso atendimento com até 24h de antecedência. Em caso de cancelamento, o valor pago será restituído de acordo com o modo de pagamento escolhido no momento da contratação (via pix ou pelo cartão de crédito). Apenas 1 (um) reagendamento sem antecedência pode ser feito por consulta.'
  },
  {
    question: 'É possível realizar a consulta estando fora do Brasil?',
    answer:
      'Sim, você pode consultar a partir de qualquer lugar, basta escolher a forma que preferir: através de ligação telefônica ou chamada de vídeo.'
  },
  {
    question: 'Como posso me preparar para minha consulta?',
    answer:
      'Para ter a melhor experiência possível com a consulta online, aqui vão nossas dicas: esteja em um lugar tranquilo, com uma conexão de internet de boa qualidade; tenha em mente tanto as informações pessoais que vão ser passadas ao médico(a) como os elementos clínicos que podem ajudá-lo a decidir sobre o seu tratamento.',
    body: (
      <>
        <Body1>Para ter a melhor experiência possível com a consulta online, aqui vão nossas dicas:</Body1>

        <Body1>
          <CheckBox w="12px" mr="6px" mb="3px" fill="phthalo" isChecked />
          Esteja em um lugar tranquilo, com uma conexão de internet de boa qualidade
        </Body1>

        <Body1>
          <CheckBox w="12px" mr="6px" mb="3px" fill="phthalo" isChecked />
          Tenha em mente tanto as informações pessoais que vão ser passadas ao médico(a) como os elementos clínicos que
          podem ajudá-lo a decidir sobre o seu tratamento.
        </Body1>
      </>
    )
  },
  {
    question: 'A Omens é confiável?',
    answer: `Sim. A Omens é uma empresa brasileira registrada no CNPJ 38.143.501/0001-08, com sede em São Paulo. As consultas são feitas por médicos com CRM e RQE, os tratamentos são preparados por farmácias certificadas e os seus dados são protegidos pela LGPD, com um encarregado de dados (DPO) indicado na [Política de privacidade](/politica-de-privacidade). No [Reclame Aqui](${OMENS_RECLAME_AQUI_URL}), a Omens tem reputação "Ótima", com 100% das reclamações respondidas (dados de 01/10/2025 a 30/09/2026).`
  },
  {
    question: 'Quanto custa uma consulta na Omens?',
    answer:
      'A consulta online custa a partir de R$ 79 e o valor depende da especialidade: por exemplo, R$ 79 para disfunção erétil, perda de libido, queda de cabelo e acne, e R$ 89 para ejaculação precoce. O preço aparece antes do pagamento, que pode ser feito por PIX ou cartão.'
  },
  {
    question: 'A consulta online tem validade?',
    answer:
      'Sim. A teleconsulta é regulamentada no Brasil pela Lei 14.510/2022 e pela Resolução CFM 2.314/2022. O médico que atende você tem registro no Conselho Regional de Medicina e, quando indicar um tratamento, faz isso como em uma consulta presencial.'
  },
  {
    question: 'Como recebo o tratamento?',
    answer:
      'O tratamento indicado na consulta é preparado por farmácias certificadas parceiras e entregue na sua casa, em embalagem discreta, sem identificação do conteúdo.'
  },
  {
    question: 'Quais médicos atendem na Omens?',
    answer:
      'Atendem na Omens urologistas, dermatologistas e psicólogos, com registro no conselho da sua profissão, como o urologista Dr. João Brunhara e a dermatologista Dra. Aline Erthal. Conheça toda a [equipe médica](/equipe-medica).'
  }
]

/** The GEO home (PRD section 7), promoted from /home-v2 on 28/10/2026: what IndexPage renders on omens.com.br. */
export const HOME_GEO: HomeGeo = {
  meta: { title: OMENS_PAGES['/'].title, description: HOME_META_DESCRIPTION },
  summary: HOME_SUMMARY,
  conditionLinks: HOME_CONDITION_LINKS,
  statsSource: HOME_STATS_SOURCE,
  datafolha: HOME_DATAFOLHA,
  faq: HOME_FAQ
}
